import PptxGenJS from 'pptxgenjs';
import { LabelValue, PPTFormattedPermitsSection } from '@/features/files/types';
import { TITLE_TO_BODY, SLIDE_BOTTOM_SAFE, SECTION_SPACING, MIN_SECTION_HEIGHT, SECTION_TITLE_HEIGHT } from '../helpers/constants';
import { PPTImageManager } from '../helpers/image-manager';
import {
    estimateBulletsHeight,
    estimateTechnologyBulletsHeight,
    getTechnologiesThatFit,
    toBulletRuns
} from '../helpers';
import {
    addBulletsSection,
    addTechnologySections,
    createBaseSlide
} from '../template-parts';

interface TechnologyBlock {
    type: string;
    collection?: string;
    fields: LabelValue[];
}

interface GeneralDetailData {
    projectTitle?: string;
    project: {
        generalInformation: LabelValue[],
        permits: PPTFormattedPermitsSection,
        technologies: TechnologyBlock[],
    };
}

interface Section {
    icon: string;
    title: string;
    content: any[];
    type: 'bullets' | 'technologies';
    estimatedHeight: number;
}


function permitsText(permits: PPTFormattedPermitsSection) {
    const { permits: permitsToRender, comments } = permits;
    let permitsBullets: any = [];

    permitsToRender.forEach((permit: any) => {
        const { label, value } = permit;
        permitsBullets.push(
            { text: `${label}: `, options: { breakLine: false, bullet: true, bold: true } },
            { text: value, options: { breakLine: true, bullet: true } }
        );
    });

    const otherCommentsText = comments ?? 'No comments provided';
    permitsBullets.push(
        { text: `Comments: `, options: { breakLine: false, bullet: true, bold: true } },
        { text: otherCommentsText, options: { breakLine: true, bullet: true } }
    );

    return permitsBullets;
}

/**
 * Prepare the sections with their content and estimated height.
 */
function prepareSections(data: GeneralDetailData, icons: any): Section[] {
    const { generalInformation, permits, technologies } = data.project;
    const sections: Section[] = [];

    // 1. General Information
    const generalInfoBullets = toBulletRuns(generalInformation);
    sections.push({
        icon: icons.folder,
        title: 'General Information',
        content: generalInfoBullets,
        type: 'bullets',
        estimatedHeight: estimateTechnologyBulletsHeight(generalInfoBullets.length)
    });

    // 2. Technologies (each one is a subsection)
    if (technologies && technologies.length > 0) {
        sections.push({
            icon: '',
            title: 'Technologies',
            content: technologies,
            type: 'technologies',
            estimatedHeight: estimateTechnologyBulletsHeight(technologies.length)
        });
    }

    // 3. Permitting
    const permittingBullets = permitsText(permits);
    sections.push({
        icon: icons.list,
        title: 'Permitting',
        content: permittingBullets,
        type: 'bullets',
        estimatedHeight: estimateBulletsHeight(permittingBullets)
    });

    return sections;
}

// ============================================
// MAIN FUNCTION WITH AUTO-PAGINATION
// ============================================

export function generalDetailsSlide(pptx: PptxGenJS, data: GeneralDetailData, imageManager: PPTImageManager) {

    const icons = {
        folder: imageManager.get('folder'),
        list: imageManager.get('list'),
        solar: imageManager.get('solar-black')
    }

    const sections = prepareSections(data, icons);
    const logoBlack = imageManager.get('logo-black');
    // Initial Y position (after the title)
    const CONTENT_START_Y = 1.71;
    let currentY = CONTENT_START_Y;
    let currentSlide = createBaseSlide(pptx, data, logoBlack, 1);
    let slideNumber = 1;

    for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        const availableHeight = SLIDE_BOTTOM_SAFE - currentY - 0.2;

        // Special case: Technologies may require multiple slides
        if (section.type === 'technologies') {
            const technologies = section.content as TechnologyBlock[];
            let techIndex = 0;

            while (techIndex < technologies.length) {
                const remainingTechs = technologies.slice(techIndex);
                const availableSpace = SLIDE_BOTTOM_SAFE - currentY - 0.2;
                const techsThatFit = getTechnologiesThatFit(remainingTechs, availableSpace);

                if (techsThatFit === 0 && currentY > CONTENT_START_Y) {
                    // There is no room for more technologies on this slide; create a new one.
                    slideNumber++;
                    currentSlide = createBaseSlide(pptx, data, logoBlack, slideNumber);
                    currentY = CONTENT_START_Y;
                    continue;
                }

                // Render the technologies that fit
                const techsToRender = remainingTechs.slice(0, techsThatFit);
                currentY = addTechnologySections(currentSlide, techsToRender, currentY, imageManager);
                techIndex += techsThatFit;

                // If there are any technologies left, create a new slide.
                if (techIndex < technologies.length) {
                    slideNumber++;
                    currentSlide = createBaseSlide(pptx, data, logoBlack, slideNumber);
                    currentY = CONTENT_START_Y;
                }
            }

            continue;
        }

        // For normal bullet sections
        if (section.estimatedHeight > availableHeight && availableHeight < MIN_SECTION_HEIGHT) {
            // Does not fit on this slide, create new
            slideNumber++;
            currentSlide = createBaseSlide(pptx, data, logoBlack, slideNumber);
            currentY = CONTENT_START_Y;
        }

        // Render the section
        const titleY = currentY;
        const bodyY = titleY + TITLE_TO_BODY;
        const remainingSpace = SLIDE_BOTTOM_SAFE - bodyY - 0.2;
        const sectionHeight = Math.min(section.estimatedHeight - SECTION_TITLE_HEIGHT, remainingSpace);

        if (sectionHeight >= MIN_SECTION_HEIGHT) {
            addBulletsSection(
                currentSlide,
                section.title,
                section.content,
                titleY,
                bodyY,
                sectionHeight,
                section.icon
            );

            currentY = bodyY + sectionHeight + SECTION_SPACING;
        }
    }
}
